import { useState, useEffect } from "react";
import { YStack, XStack, Text, View } from "tamagui";
import { colors } from "@styles/colors";
import { Header, Fade, RadioCard, RADIUS } from "./Common";
import { Ic, Star, TankIcon } from "./Icons";
import RadiusContentWrapper from "@components/layout/RadiusContentWrapper";

const VENDORS = [
  {
    name: "中區瓦斯行",
    dist: 0.8,
    eta: "30 分內",
    rating: 4.7,
    reviews: 1240,
    prices: "16kg $660 ／ 20kg $820",
    tags: ["假日不加價", "24 小時叫桶", "電子發票"],
    top: true,
    reason: "距離最近・評價最高",
  },
  {
    name: "北區瓦斯行",
    dist: 1.5,
    eta: "40 分內",
    rating: 4.4,
    reviews: 680,
    prices: "16kg $670 ／ 20kg $830",
    tags: ["LINE 通知", "電子發票"],
    top: false,
  },
  {
    name: "南屯瓦斯行",
    dist: 2.3,
    eta: "50 分內",
    rating: 4.6,
    reviews: 520,
    prices: "16kg $680 ／ 20kg $840",
    tags: ["熟客累點", "可開公司戶"],
    top: false,
  },
  { name: "城市瓦斯快遞", dist: 3.1, eta: "60 分內", rating: 4.3, reviews: 380, prices: "16kg $690 ／ 20kg $860", tags: ["全區配送"], top: false },
];

function VendorCard({ v, selected, onSelect }: any) {
  return (
    <RadioCard selected={selected} onSelect={onSelect} accent={colors.primary}>
      <YStack>
        <XStack ai="center" gap="$2" mb="$1.5" fw="wrap">
          <Text fontSize={15} fontWeight="800" color={colors.grayDark}>
            {v.name}
          </Text>
          <XStack ai="center" gap="$1">
            <Star />
            <Text fontSize={11} color={colors.amberDark} fontWeight="700">
              {v.rating}
            </Text>
            <Text fontSize={10} color={colors.gray200}>
              （{v.reviews}）
            </Text>
          </XStack>
        </XStack>
        <XStack gap="$2.5" mb="$2" fw="wrap">
          <XStack ai="center" gap="$1">
            <Ic d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" c={colors.gray200} s={11} sw={2} />
            <Text fontSize={11} color={colors.gray}>
              {v.dist} km
            </Text>
          </XStack>
          <XStack ai="center" gap="$1">
            <Ic d="M12 2v10l4.5 4.5" c={colors.green} s={11} sw={2} />
            <Text fontSize={11} color={colors.homeGreen} fontWeight="700">
              {v.eta}
            </Text>
          </XStack>
        </XStack>
        <View bg={colors.bg} br={8} px="$2.5" py="$1.5" mb="$2">
          <Text fontSize={11} color={colors.gray} fontWeight="600">
            {v.prices}
          </Text>
        </View>
        <XStack gap="$1.5" fw="wrap">
          {v.tags.map((t: string) => (
            <View key={t} bg={v.top ? colors.bgAmber : colors.primaryActive} px="$2" py="$0.5" br={6}>
              <Text fontSize={10} fontWeight="700" color={v.top ? colors.amberDark : colors.primary}>
                {t}
              </Text>
            </View>
          ))}
        </XStack>
      </YStack>
    </RadioCard>
  );
}

export default function StepSelect({ onBack }: { onBack: () => void }) {
  const [sel, setSel] = useState(VENDORS[0].name);

  useEffect(() => {
    window.dispatchEvent(new Event("scroll-to-top"));
  }, []);

  const top = VENDORS.find((v) => v.top);
  const others = VENDORS.filter((v) => !v.top);

  return (
    <RadiusContentWrapper padding="0" space={0} background="transparent" contentContainerStyle={{ paddingBottom: 120 }}>
      <Header title="選一家瓦斯行" sub={`步驟 3 / 3　找到 ${VENDORS.length} 家可配送`} step={3} total={3} onBack={onBack} />

      <YStack p="$5" gap="$4">
        {top && (
          <Fade i={0}>
            <YStack gap="$2.5">
              <XStack ai="center" gap="$1.5" pl={2}>
                <Star s={13} />
                <Text fontSize={12} fontWeight="700" color={colors.gray}>
                  最推薦
                </Text>
                <Text fontSize={11} color={colors.gray200}>
                  · {top.reason}
                </Text>
              </XStack>
              <VendorCard v={top} selected={sel === top.name} onSelect={() => setSel(top.name)} />
            </YStack>
          </Fade>
        )}

        <Fade i={1}>
          <YStack gap="$2.5">
            <Text fontSize={12} fontWeight="700" color={colors.gray} pl={2}>
              其他可配送的瓦斯行（{others.length}）
            </Text>
            <YStack gap="$2.5">
              {others.map((v) => (
                <VendorCard key={v.name} v={v} selected={sel === v.name} onSelect={() => setSel(v.name)} />
              ))}
            </YStack>
          </YStack>
        </Fade>

        <Fade i={2}>
          <XStack bg={colors.primaryActive} br={RADIUS} p="$3.5" gap="$2.5">
            <Ic d="M12 9v4M12 17h.01M21 12A9 9 0 113 12a9 9 0 0118 0z" c={colors.primary} s={15} />
            <Text f={1} fontSize={11} color={colors.primary} lineHeight={16}>
              選定後建立你在該瓦斯行的客戶帳號，首次叫桶時送貨員到府完成開戶。
            </Text>
          </XStack>
        </Fade>
      </YStack>

      <YStack backgroundColor={colors.bg} style={{ position: "sticky", bottom: "0", width: "100%", zIndex: 100 }} p="$4">
        <XStack
          className="primary-bg-gradient"
          borderRadius={20}
          p={15}
          alignItems="center"
          justifyContent="center"
          opacity={sel ? 1 : 0.5}
          cursor={sel ? "pointer" : "default"}
          onPress={() => sel && alert(`已建立「${sel}」帳戶！`)}
          style={{ boxShadow: sel ? `0 4px 16px ${colors.primary}40` : "none" }}
          pressStyle={{ opacity: 0.8 }}>
          <XStack gap={10} ai="center">
            <TankIcon c={sel ? "#fff" : colors.gray200} s={20} />
            <Text color="white" fontSize={16} fontWeight="bold">
              {sel ? `用「${sel}」立即下單` : "請選擇瓦斯行"}
            </Text>
          </XStack>
        </XStack>
      </YStack>
    </RadiusContentWrapper>
  );
}
